import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { cognitoTokenProvider } from '../auth/tokenProvider';
import {
  cognitoIdentityProvider,
  type Identity,
} from '../auth/identityProvider';

/**
 * React auth binding. Thin state/action layer OVER the pure-TS
 * {@link cognitoTokenProvider} + {@link cognitoIdentityProvider} singletons —
 * the SAME instances the API facade uses, so there is one source of truth.
 *
 * Components consume auth ONLY through {@link useAuth}; they never import the
 * providers directly.
 */

export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

export interface AuthContextValue {
  status: AuthStatus;
  identity: Identity | null;
  /** Redirects to the hosted UI (AmazonFederate) to begin sign-in. */
  signIn: () => void;
  /** Clears the session and redirects to hosted logout. */
  signOut: () => void;
  /** Completes the Authorization-Code exchange on the callback route. */
  completeSignIn: () => Promise<void>;
  /** Re-derives identity from the current token. */
  refreshIdentity: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [identity, setIdentity] = useState<Identity | null>(null);

  const refreshIdentity = useCallback(async () => {
    const id = await cognitoIdentityProvider.getIdentity();
    setIdentity(id);
  }, []);

  const syncFromStorage = useCallback(async () => {
    if (cognitoTokenProvider.isAuthenticated()) {
      await refreshIdentity();
      setStatus('authenticated');
    } else {
      setIdentity(null);
      setStatus('unauthenticated');
    }
  }, [refreshIdentity]);

  // Bootstrap on mount.
  useEffect(() => {
    void syncFromStorage();
  }, [syncFromStorage]);

  const signIn = useCallback(() => {
    void cognitoTokenProvider.login();
  }, []);

  const signOut = useCallback(() => {
    cognitoTokenProvider.logout();
    // logout() redirects for real sessions; reflect state locally regardless.
    setIdentity(null);
    setStatus('unauthenticated');
  }, []);

  const completeSignIn = useCallback(async () => {
    await cognitoTokenProvider.handleRedirectCallback();
    await syncFromStorage();
  }, [syncFromStorage]);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      identity,
      signIn,
      signOut,
      completeSignIn,
      refreshIdentity,
    }),
    [status, identity, signIn, signOut, completeSignIn, refreshIdentity],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Returns the auth context. Throws if used outside <AuthProvider>. */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an <AuthProvider>');
  }
  return ctx;
}
